/**
 * The one place a submission actually crosses the network.
 *
 * Everything above this speaks in domain terms. Swapping how records travel -- a
 * different endpoint shape, a batched upload, a signed envelope -- touches this file
 * and nothing else.
 *
 * Note what is absent: no retry loop, no backoff, no queue. This reports what
 * happened and returns. Deciding what to do about it belongs to the outbox, where it
 * is pure and tested.
 */

import type { OutboxRecord, PhotoRecord } from './idb-schema.ts';
import type { SendResult, Transport } from './sync.ts';

interface AcceptedResponse {
  readonly id: number;
  readonly receivedAt: number;
}

const SUBMISSION_PATHS: Record<OutboxRecord['kind'], string> = {
  inspection: '/api/op/inspections',
  problem: '/api/op/problems',
  unknown_tag: '/api/scan/unknown',
};

export function httpTransport(): Transport {
  return {
    async sendSubmission(record) {
      return post(SUBMISSION_PATHS[record.kind], {
        // The client id is the idempotency key. The server inserts on
        // conflict-do-nothing, so a retry after an ambiguous timeout costs nothing
        // and cannot create a second record.
        clientId: record.id,
        shortCode: record.shortCode,
        assetId: record.assetId,
        operatorId: record.operatorId,
        occurredAtDeviceMs: record.occurredAtDeviceMs,
        clockOffsetMsAtCaptureMs: record.clockOffsetMsAtCaptureMs,
        offsetAgeMs: finiteOrNull(record.offsetAgeMs),
        photoCount: record.photoIds.length,
        payload: record.payload,
      });
    },

    async sendPhoto(photo) {
      return postPhoto(photo);
    },
  };
}

async function post(path: string, body: unknown): Promise<SendResult> {
  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

    return await interpret(response);
  } catch (error) {
    // No status at all: the request never reached a server. In a metal building this
    // is the ordinary case, not an exception.
    return { ok: false, reason: describe(error) };
  }
}

async function postPhoto(photo: PhotoRecord): Promise<SendResult> {
  const form = new FormData();
  form.append('photo', photo.blob, `${photo.id}.jpg`);
  form.append('outboxId', photo.outboxId);

  try {
    const response = await fetch(`/api/op/photos/${photo.id}`, { method: 'POST', body: form });
    return await interpret(response);
  } catch (error) {
    return { ok: false, reason: describe(error) };
  }
}

async function interpret(response: Response): Promise<SendResult> {
  if (!response.ok) {
    return { ok: false, reason: `HTTP ${response.status}`, httpStatus: response.status };
  }

  const accepted = (await response.json()) as AcceptedResponse;
  return { ok: true, serverId: accepted.id, serverReceivedAtMs: accepted.receivedAt };
}

/**
 * `Infinity` is not valid JSON -- JSON.stringify turns it into null anyway, silently.
 * Doing it here makes the intent explicit: the server reads null as "this device has
 * never had a clock reading" rather than receiving a surprise.
 */
function finiteOrNull(value: number): number | null {
  return Number.isFinite(value) ? value : null;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : 'network request failed';
}
